import { EIntakeFormStatus } from './calendar';
import { EIntakeFormType, Prospect, TAllergy, TPharmacy } from './common';
import type { IProviderSignature, TVisitBillingInfo } from './treatment';

export interface TextVisitAttachment {
    id?: number;
    textVisitId?: number;
    path: string;
    name?: string;
    type?: string;
    sort?: number;
    createdBy?: number;
    createdAt?: string;
    updatedBy?: number;
    updatedAt?: string;
    previewUrl?: string; // 前端本地预览用
}

export enum EBusinessType {
    APPOINTMENT = 'APPOINTMENT',
    TEXT_VISIT = 'TEXT_VISIT',
    STANDALONE = 'STANDALONE',
}

export interface ITreatmentPlan {
    id: number;
    textVisitId: number;
    treatmentPlan: string;
    providerNote: string;
    aiTreatmentDraftStatus: boolean;
    autoFollowUp: boolean;
    autoFollowUpInterval: number;
    autoFollowUpIntervalUnit: 'D' | 'W' | 'M';
    autoFollowUpMessage: string;
    attachments: TextVisitAttachment[];
    submit: boolean;
    prescriptions?: TPrescriptionListItem[];
    klarityBillingNote?: string;
    diagnoses?: import('types/insuranceBilling').TProviderVisitDiagnosisVo[];
    billingCodes?: import('types/insuranceBilling').TProviderVisitBillingCodeVo[];
}

export interface FollowUp {
    id: number | null;
    textVisitId: number | null;
    senderType: string | null;
    senderId: number | null;
    message: string | null;
    createdAt: string | null;
    updatedAt: string | null;
}

export interface IntakeQuestion {
    id: number | null;
    textVisitId: number | null;
    questionType: string | null;
    fullQuestionName: string | null;
    shortQuestionName: string | null;
    answer: string | null;
    sort: number | null;
    createdAt: string | null;
    updatedAt: string | null;
}

export enum TextVisitServiceType {
    INITIAL_VISIT = 'INITIAL_VISIT',
    MEDICATION_REFILL = 'MEDICATION_REFILL',
    MEDICATION_CHANGE = 'MEDICATION_CHANGE',
    FOLLOW_UP_CONSULTATION = 'FOLLOW_UP_CONSULTATION',
    OTHER = 'OTHER',
    PRESCRIPTION_REFILL = 'PRESCRIPTION_REFILL',
}

export enum IPaymentStatus {
    SUCCEEDED = 'SUCCEEDED',
    REFUNDED = 'REFUNDED',
    REFUNDED_FAILED = 'REFUNDED_FAILED',
    REFUNDED_PROCESSING = 'REFUNDED_PROCESSING',
}

export type TextVisitSnoozeReq = {
    snoozeUntil: string;
    textVisitId: number;
};
export type TFormAnswer = {
    id: number;
    klarityUserFormId: number;
    sectionName: string;
    fullQuestionName: string;
    fullQuestionNameMd5: string;
    shortQuestionName: string;
    answer: string;
    score: number;
    totalScore: number;
    sort: number;
    createdAt: string;
    updatedAt: string;
};

export type TFormGroup = {
    answers: TFormAnswer[];
    businessType: EBusinessType;
    businessId: number;
    formType: EIntakeFormType;
    serviceItemId?: number;
    serviceType?: string;
    specialty?: string;
};

// {
//     "id": 24,
//     "klarityUserId": 21,
//     "fullName": "12",
//     "phone": "9086958801",
//     "relationship": "parent",
//     "priority": 1,
//     "createAt": "2026-02-02T15:42:02",
//     "updateAt": "2026-02-02T15:42:02"
//   }
export type TEmergencyContact = {
    id: number;
    klarityUserId: number;
    fullName: string;
    phone: string;
    relationship: string;
    priority: number;
    createAt: string;
    updateAt: string;
};

// {
//     "id": 154,
//     "cardLast4": "4242",
//     "cardBrand": "visa",
//     "cardType": "CREDIT",
//     "isDefault": true,
//     "paymentChannelCode": "STRIPE",
//     "paymentMethodType": "CREDIT_CARD"
// }
export type TPaymentMethod = {
    id: number;
    cardLast4: string;
    cardBrand: string;
    cardType: string;
    isDefault: boolean;
    paymentChannelCode: 'SQUARE' | 'STRIPE' | 'BRAINTREE' | 'STRIPE';
    paymentMethodType: 'CREDIT_CARD' | 'PAYPAL' | 'BANK_ACCOUNT' | 'BRAINTREE';
};
export interface TextVisitCase {
    id: number;
    textVisitUuid: string;
    klarityUserId: number;
    clientId: number; // patientId
    patientFirstName: string | null;
    patientLastName: string | null;
    patientGender: string | null;
    patientBirthday: string | null;
    patientIdFrontUrl: string | null;
    patientIdBackUrl: string | null;
    patientAge: number | null;
    prospect: Prospect;
    providerId: number;
    state: string | null;
    conditions: string | null;
    paymentStatus: IPaymentStatus | null;
    idVerified: boolean;
    patientSubmitAt: string | null;
    providerTreatmentSubmitted: boolean;
    providerTreatmentSubmittedAt: string | null;
    // Hotfix 2026-05-12: prescription refill payment hold fields
    noteSendStatus?: 'NOT_APPLICABLE' | 'SENT' | 'PENDING_PAYMENT';
    noteSentAt?: string | null;
    noteHoldReason?: 'NO_PAYMENT_METHOD' | 'BILLING_FAILED' | 'INVOICE_UNPAID' | null;
    payableInvoiceNo?: string | null; // detail only
    patientChargeAmount?: number | null; // detail only
    status: string | null;
    updatedAt: string | null;
    assignedProviderId: number | null;
    assignedProviderFirstName: string | null;
    assignedProviderLastName: string | null;
    assignedProviderPhoto: string | null;
    assignedProviderAt: string | null;
    aiSummary: string | null;
    aiTags: string | null;
    historyMedical: string[] | null;
    currentMedications: string[] | null;
    allergies: string[] | null;
    followUps?: FollowUp[];
    treatmentPlan?: ITreatmentPlan;
    intakeQuestions?: IntakeQuestion[];
    pharmacySelected: boolean;
    payoutAmount: number;
    serviceType: TextVisitServiceType;
    patientAdditionalComment?: string;
    prescriptions?: TPrescriptionListItem[];
    assessmentName: string;
    conditionHealth?: {
        featured: string[];
        notSure: boolean;
    };
    medicationHealth?: {
        medications: string[];
        notTaking: boolean;
        others: string[];
    };
    allergyHealth?: {
        hasAllergy: boolean;
        allergies: TAllergy[];
    };
    pharmacy?: TPharmacy;
    formGroups: TFormGroup[];
    maritalStatus?: string | null;
    emergencyContacts: TEmergencyContact[];
    intakeFormStatus?: EIntakeFormStatus;
    paymentMethods?: TPaymentMethod[];
    hasSentInvoice?: boolean;
    specialty?: string;
    providerSignature?: IProviderSignature;
    visitBilling?: TVisitBillingInfo;
}

export interface CaseListFilter {
    states: string[];
    gender: string[];
    patientAge: string[]; //PEDIATRIC  ADULT  SENIOR
    sortBy: string; //OLDEST. NEWEST
    current: number;
    size: number;
}

export interface MyCaseFilter {
    status: ICaseStatus;
    treatmentSubmitted?: boolean;
    sortBy?: string; // MOST_URGENT, OLDEST, NEWEST, etc.
    size: number;
    current: number;
}

export enum ICaseStatus {
    PENDING = 'PENDING',
    UNASSIGNED = 'UNASSIGNED',
    ACTIVE = 'ACTIVE',
    CLOSED = 'CLOSED',
    CANCELED = 'CANCELED',
    COMPLETED = 'COMPLETED',
}

export enum IRefundStatus {
    PENDING_REVIEW = 'PENDING_REVIEW',
    APPROVE_FULL = 'APPROVE_FULL',
    APPROVE_PARTIAL = 'APPROVE_PARTIAL',
    DENIED = 'DENIED',
    AUTO_APPROVED = 'AUTO_APPROVED',
}

export enum IRefundExecutionStatus {
    PENDING_REVIEW = 'PENDING_REVIEW',
    WAITING_REFUNDED = 'WAITING_REFUNDED',
    IN_PROGRESS = 'IN_PROGRESS',
    REFUNDED = 'REFUNDED',
    FAILED = 'FAILED',
    DENIED = 'DENIED',
}
export interface CaseListItem {
    id: number;
    textVisitUuid: string;
    klarityUserId: number;
    aiSummary: string;
    state: string;
    conditions: string;
    patientFirstName?: string;
    patientLastName: null;
    patientEmail?: string;
    patientGender: string;
    patientBirthday: string;
    status: ICaseStatus;
    paymentStatus?: IPaymentStatus;
    patientSubmitAt?: string;
    providerTreatmentSubmittedAt?: string;
    providerTreatmentSubmitted?: boolean;
    assignedProviderAt?: string;
    assignedProviderId?: number;
    assignedProviderFirstName?: string;
    assignedProviderLastName?: string;
    assignedProviderPhoto?: string;
    providerType?: string;
    providerUnreadCount?: number;
    updatedAt: string;
    startAt?: string;
    endAt?: string;
    serviceType: TextVisitServiceType;
    specialty?: string;
    refundStatus: IRefundStatus; // ops 使用的字段
    refundExecutionStatus: IRefundExecutionStatus;
    refundAmount: number;
    refundReason: string;
    refundRequestId?: number;
    notesInternal?: string;
    notesToPatient?: string;
    notesToProvider: string;
    icon: string;
    closedAt: string;
    assessmentName: string;
    intakeFormStatus: EIntakeFormStatus;
    clientId?: number;
    billingId?: number;
    recurrenceId?: number;
    recurrenceType?: string;
    endOccurrences?: number;
    occurrenceIndex?: number;
    noteSendStatus?: 'NOT_APPLICABLE' | 'SENT' | 'PENDING_PAYMENT';
    noteHoldReason?: 'NO_PAYMENT_METHOD' | 'BILLING_FAILED' | 'INVOICE_UNPAID' | null;
    canModifyRecurrence?: boolean;
    provider?: {
        providerId: number;
        firstName: string;
        lastName: string;
        avatar: string;
    };
}

export type TScriptSureSessiontoken = {
    bpProviderId: number;
    sessionToken: string;
    ssEmail: string;
    ssProviderId: number;
};

export enum EPrescriptionListSortBy {
    EXPIRES = 'EXPIRES',
    WRITTEN = 'WRITTEN',
}

export enum EPrescriptionListStatus {
    ACTIVE = 'Active',
    QUEUED = 'Queued',
}
export interface TPrescriptionListFilter {
    patientName?: string;
    status?: EPrescriptionListStatus[]; //Active,Queued
    drugName?: string; //PEDIATRIC  ADULT  SENIOR
    sortBy?: EPrescriptionListSortBy; //EXPIRES WRITTEN
    sortType?: 'ASC' | 'DESC';
    current: number;
    size: number;
}

export type TPrescriptionSig = {
    id: number | null;
    prescriptionId: number | null;
    drugId: number | null;
    sigOrder: number | null;
    line3: string;
    doseDeliveryMethodText: string | null;
    doseQuantity: number | null;
    doseFormText: string | null;
    routeOfAdministrationText: string | null;
    administrationTimingText: string | null;
    frequencyNumericValue: number | null;
    frequencyUnitsText: string | null;
    descriptor: string | null;
    calculate: boolean | null;
    createdAt: string | null;
};
export type TPrescriptionListItem = {
    prescriptionId: number;
    bpProviderId: number;
    bpClientId: number;
    identityId?: number;
    clientFirstName: string | null;
    clientLastName: string | null;
    drugId: number;
    drugName: string;
    drugLine1: string;
    drugLine2: string;
    drugSigs: TPrescriptionSig[];
    drugFormat: string;
    refill: number;
    duration: number;
    quantity: string;
    fillDate: string;
    approveDate: string | null;
    status: EPrescriptionListStatus;
    statusTypeId: number;
    writtenDate: string;
    expires: string;
    pharmacyId: number;
    pharmacyBusinessName: string;
    pharmacyAddressLine1: string;
    pharmacyAddressLine2: string;
    pharmacyCity: string;
    pharmacyState: string;
    pharmacyPostalCode: string;
    pharmacyPhone: string;
    pharmacyFax: string;
    practiceAddress1: string; //'5 Science Park Drive, Shopee Buildi'
    practiceAddress2: string; //'line2'
    practiceCity: string; //'sg'
    practiceFax: string; //'1212121212'
    practiceName: string; //'World Saving'
    practicePhone: string; //'1212121212'
    practiceState: string; //'AK'
    practiceZipcode: string; //'1212121212'
};

export type TPracticeState = {
    practiceName?: string;
    state: string; //'FL',
    city?: string;
    zipCode?: string;
    address?: string;
    addressLine2?: string;
    enableEpcs?: boolean;
};

// Practice-view (Owner / PM / Assistant) — org-wide, scope-respecting text-visit list

export type TPracticeListStatus = 'in-progress' | 'active' | 'closed';

export interface TPracticeListFilter {
    current?: number;
    size?: number;
    orderKey?: string;
    orderType?: string;
    providerId?: number | null; // null = all providers in actor scope; non-null = single-provider filter
    status?: TPracticeListStatus; // tab being rendered; ignored by the counts endpoint
}

export interface TPracticeListItem {
    id: number;
    textVisitUuid: string;
    klarityUserId: number;
    status: ICaseStatus; // PENDING | UNASSIGNED | ACTIVE | CLOSED | CANCELED
    serviceType: TextVisitServiceType;
    assessmentName: string;
    conditions: string;
    icon: string;
    state: string;
    // patient (PHI)
    patientFirstName: string;
    patientLastName: string;
    patientGender: string;
    patientBirthday: string;
    patientSubmitAt: string;
    // assigned provider
    assignedProviderId: number;
    assignedProviderFirstName: string;
    assignedProviderLastName: string;
    assignedProviderCredential: string;
    assignedProviderHeadshot: string;
    assignedProviderAt: string;
    providerType: 'K' | 'E' | string; // K = PPA (per-patient), E = PPV (per-visit)
    providerTreatmentSubmittedAt: string;
    providerUnreadCount: number;
    // lifecycle window
    startAt: string;
    endAt: string;
    closedAt: string;
    updatedAt: string;
    // billing / refund
    paymentStatus: string;
    refundStatus: string;
    refundExecutionStatus: string;
    refundAmount: number;
    refundReason: string;
    notesToProvider: string;
    // AI summary
    aiSummary: string;
    aiSummaryContextMd5: string;
    aiSummaryGenerateAt: string;
}

export type TPracticeListResponse = {
    code: number;
    message: string;
    data: {
        pageNo: number;
        pageSize: number;
        totalRecords: number;
        isLast: boolean;
        list: TPracticeListItem[];
    };
};

export type TPracticeListCounts = {
    inProgressCount: number;
    activeCount: number;
    closedCount: number;
};

export type TPracticeRemindProviderReq = {
    message: string;
    textVisitId?: number;
};
